import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ICompasAIChatAction } from "@/types/compass.types";
import {
  isActiveStatus,
  parseQueueMessage,
  processTaskUpdate,
  taskKey,
  type TaskUpdateResult,
} from "./compass-queue.utils";

export interface ApplyDeps {
  taskStatusesRef: MutableRefObject<Record<string, string>>;
  invalidationTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  callbacksRef: MutableRefObject<{ onUpdate?: () => void; onMessage?: () => void }>;
  setTaskStatuses: Dispatch<SetStateAction<Record<string, string>>>;
  setActiveProcessingTask: Dispatch<SetStateAction<ICompasAIChatAction | null>>;
  queryClient: QueryClient;
  docId: string;
}

export function toastTransitions(result: TaskUpdateResult): void {
  if (result.completedCount > 0) {
    toast.success(
      result.completedCount > 1 ? `${result.completedCount} actions completed` : "Action completed",
      { duration: 2000 }
    );
  }
  if (result.failedCount > 0) {
    toast.error(result.failedCount > 1 ? `${result.failedCount} actions failed` : "Action failed", {
      duration: 2000,
    });
  }
}

export function scheduleHistoryInvalidation(deps: ApplyDeps, result: TaskUpdateResult): void {
  if (!result.hasCompletion) return;
  if (deps.invalidationTimeoutRef.current != null) {
    clearTimeout(deps.invalidationTimeoutRef.current);
  }
  deps.invalidationTimeoutRef.current = setTimeout(() => {
    void deps.queryClient.invalidateQueries({ queryKey: ["chatHistory", deps.docId] });
    void deps.queryClient.invalidateQueries({ queryKey: ["compass-chat-history", deps.docId] });
    const currentHasActive = Object.values(deps.taskStatusesRef.current).some(isActiveStatus);
    if (!currentHasActive && result.wasProcessing) {
      deps.callbacksRef.current.onUpdate?.();
    }
    if (!currentHasActive) {
      deps.setActiveProcessingTask(null);
    }
  }, 500);
}

export function applyTaskResult(deps: ApplyDeps, result: TaskUpdateResult): void {
  toastTransitions(result);
  deps.taskStatusesRef.current = result.currentStatuses;
  deps.setTaskStatuses(result.currentStatuses);

  const hasActiveTasks = Object.values(result.currentStatuses).some(isActiveStatus);
  scheduleHistoryInvalidation(deps, result);

  if (result.processingTasks.length > 0) {
    deps.setActiveProcessingTask(result.processingTasks[0] ?? null);
    deps.callbacksRef.current.onMessage?.();
    return;
  }
  if (!hasActiveTasks) {
    deps.setActiveProcessingTask(null);
    return;
  }
  clearFinishedActiveTask(deps, result);
}

/**
 * Nothing is processing but tasks are still queued or pending. The previous
 * active task is only still current if its own status is still `processing`;
 * otherwise it finished and must not stay on screen until the next
 * processing update arrives.
 */
function clearFinishedActiveTask(deps: ApplyDeps, result: TaskUpdateResult): void {
  deps.setActiveProcessingTask((current) => {
    if (current == null) return null;
    const key = taskKey(current);
    if (key === undefined) return null;
    return result.currentStatuses[key] === "processing" ? current : null;
  });
}

export function handleSocketMessage(event: MessageEvent, deps: ApplyDeps): void {
  if (typeof event.data !== "string") return;
  const message = parseQueueMessage(event.data);
  if (message == null) return;
  const mode = message.type === "update" ? "delta" : "snapshot";
  if (mode === "delta" && message.tasks.length === 0) return;
  const result = processTaskUpdate(message.tasks, deps.taskStatusesRef.current, mode);
  applyTaskResult(deps, result);
}

export function applyHistoryActions(actions: ICompasAIChatAction[], deps: ApplyDeps): void {
  if (actions.length === 0) return;
  const result = processTaskUpdate(actions, deps.taskStatusesRef.current, "delta");
  applyTaskResult(deps, result);
}
